import React, { FC } from 'react';
import styled from '@emotion/styled';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useI18n, useLogger, usePrinterApi, useDispatch } from '~Hooks';
import * as actions from '~actions';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import { CircleSpinner } from 'react-spinners-kit';
import { handyBreakpoint } from '~Styles/breakpoints';

const CLOUD_PRINT_JOBS_QUERY = gql`
  query GetCloudPrintJobs($venueId: String!) {
    operativeViewer {
      id
      cloudPrintJobs(venueId: $venueId) {
        id
        printerId
        venueId
        status
        mediaType
        printType
        orderId
        userId
        createdAt
        pickedUpAt
        completedAt
        resultCode
        resultMessage
      }
    }
  }
`;

const REPRINT_MUTATION = gql`
  mutation ReprintCloudJob($jobId: String!) {
    reprintCloudJob(jobId: $jobId) {
      success
      message
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 0;
  box-sizing: border-box;

  ${handyBreakpoint} {
    padding: 0;
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr 1fr 100px 100px 1fr 100px;
  gap: 8px;
  padding: 12px 16px;
  background-color: var(--colorBlueGray1300);
  color: var(--colorWhite);
  border-radius: 4px 4px 0 0;
  font-size: 14px;
  align-items: center;
  font-weight: bold;

  ${handyBreakpoint} {
    display: none;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr 1fr 100px 100px 1fr 100px;
  gap: 8px;
  padding: 12px 16px;
  background-color: var(--colorBlueGray1100);
  color: var(--colorWhite);
  border-radius: 4px;
  font-size: 14px;
  align-items: center;

  ${handyBreakpoint} {
    grid-template-columns: repeat(3, 1fr);
    align-items: center;

    > span:first-child {
      grid-column: 1 / -1;
    }
  }
`;

const ReprintButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background-color: var(--colorBlueGray900);
  color: var(--colorWhite);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 11px;
  color: ${({ color }) => color};
  border: 1px solid ${({ color }) => color};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
`;

function formatTime(isoString: string | null): string {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Calculates the duration between two ISO timestamps in a human-readable format.
 * Returns null if either timestamp is missing.
 */
function getDuration(start: string | null, end: string | null): string | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

interface CloudPrintJob {
  id: string;
  printerId: string;
  venueId: string;
  status: string;
  mediaType: string;
  printType: string | null;
  orderId: string | null;
  userId: string | null;
  createdAt: string;
  pickedUpAt: string | null;
  completedAt: string | null;
  resultCode: string | null;
  resultMessage: string | null;
}

const CloudPrintHistory: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('cloud-print-history');
  const printerApi = usePrinterApi();
  const dispatch = useDispatch();
  const [, , venueId] = location.hash.split('/');

  const jobsQuery = useQuery(CLOUD_PRINT_JOBS_QUERY, {
    variables: { venueId },
    pollInterval: 3000,
    fetchPolicy: 'network-only',
  });

  const [reprintJob, reprintMutation] = useMutation(REPRINT_MUTATION, {
    refetchQueries: ['GetCloudPrintJobs'],
  });

  async function handleReprint(jobId: string) {
    try {
      logger.info('Reprinting cloud job', { jobId });
      await reprintJob({ variables: { jobId } });
      dispatch(actions.addToast(i18n('Settings.CloudPrintHistory.ReprintQueued'), true));
    } catch (err) {
      logger.error('Failed to reprint cloud job', { jobId });
      dispatch(actions.addToast(i18n('Settings.CloudPrintHistory.ReprintFailed'), false));
    }
  }

  const jobs: CloudPrintJob[] = jobsQuery.data?.operativeViewer?.cloudPrintJobs ?? [];

  if (jobsQuery.loading && !jobsQuery.data) {
    return (
      <Wrapper>
        <Box display="flex" justifyContent="center" padding="xl">
          <CircleSpinner size={32} color="#fff" />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading level={1} color="#fff">
        {i18n('Settings.CloudPrintHistory')}
      </Heading>
      <Paragraph level={3} color="var(--colorBlueGray200)" top={8}>
        {i18n('Settings.CloudPrintHistory.Description')}
      </Paragraph>

      {jobs.length === 0 ? (
        <EmptyState>
          <Paragraph level={3} color="var(--colorBlueGray400)">
            {i18n('Settings.CloudPrintHistory.Empty')}
          </Paragraph>
        </EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <span>{i18n('Settings.CloudPrintHistory.Time')}</span>
            <span>{i18n('Settings.CloudPrintHistory.Type')}</span>
            <span>{i18n('Settings.CloudPrintHistory.Printer')}</span>
            <span>{i18n('Settings.CloudPrintHistory.Status')}</span>
            <span>{i18n('Settings.CloudPrintHistory.Duration')}</span>
            <span>{i18n('Settings.CloudPrintHistory.Result')}</span>
            <span></span>
          </TableHeader>
          {jobs.map((job) => {
            const printerName = printerApi.getNameFromId(job.printerId) ?? job.printerId;
            const duration = getDuration(job.createdAt, job.completedAt ?? job.pickedUpAt);

            const resultCode =
              job.resultCode && job.resultMessage
                ? `${job.resultCode}: ${job.resultMessage}`
                : job.resultCode
                ? job.resultCode
                : '-';

            const getColorForStatus = (status: string) => {
              switch (status) {
                case 'completed':
                  return '#61e761';
                case 'picked_up':
                  return '#eeeea8';
                case 'pending':
                  return '#9bb4ea';
                case 'error':
                  return '#ff5d5d';
                default:
                  return '#c1c1c1';
              }
            };

            const getStatusText = (status: string) => {
              switch (status) {
                case 'completed':
                  return i18n('Settings.CloudPrintHistory.StatusCompleted');
                case 'picked_up':
                  return i18n('Settings.CloudPrintHistory.StatusPickedUp');
                case 'pending':
                  return i18n('Settings.CloudPrintHistory.StatusPending');
                case 'error':
                  return i18n('Settings.CloudPrintHistory.StatusError');
                default:
                  return status;
              }
            };

            const getTypeText = (type: string | null) => {
              switch (type) {
                case 'RECEIPT':
                  return i18n('Settings.CloudPrintHistory.TypeReceipt');
                case 'BONG':
                  return i18n('Settings.CloudPrintHistory.TypeKitchen');
                case 'START_DAY':
                  return i18n('Settings.CloudPrintHistory.TypeLabel');
                case 'END_DAY':
                  return i18n('Settings.CloudPrintHistory.TypeEndDay');
                case 'INVENTORY':
                  return i18n('Settings.CloudPrintHistory.TypeInventory');
                case 'TEST_PRINT':
                  return i18n('Settings.CloudPrintHistory.TypeTestPrint');
                case 'Z_REPORT':
                  return i18n('Settings.CloudPrintHistory.TypeZReport');
                case 'EPOS_ORDER_NUMBER':
                  return i18n('Settings.CloudPrintHistory.TypeEposOrderNumber');
                case 'REPORT':
                  return i18n('Settings.CloudPrintHistory.TypeReport');
                case 'GIFT_CARD':
                  return i18n('Settings.CloudPrintHistory.TypeGiftCard');
                case 'SWISH':
                  return i18n('Settings.CloudPrintHistory.TypeSwish');
                case 'PAYMENT_SCAN':
                  return i18n('Settings.CloudPrintHistory.TypePaymentScan');
                case 'CASH_CHANGE':
                  return i18n('Settings.CloudPrintHistory.TypeCashChange');
                case 'COMPETITION_SCORE':
                  return i18n('Settings.CloudPrintHistory.TypeCompetitionScore');
                default:
                  return type ?? '-';
              }
            };

            return (
              <Row key={job.id}>
                <span>
                  {formatDate(job.createdAt)} {formatTime(job.createdAt)}
                </span>
                <span>{getTypeText(job.printType)}</span>
                <span title={job.printerId}>{printerName}</span>
                <span>
                  <StatusBadge color={getColorForStatus(job.status)}>
                    {getStatusText(job.status)}
                  </StatusBadge>
                </span>
                <span>{duration ?? '-'}</span>
                <span>{resultCode}</span>
                <span>
                  <ReprintButton
                    onClick={() => handleReprint(job.id)}
                    disabled={reprintMutation.loading}>
                    {i18n('Settings.CloudPrintHistory.Reprint')}
                  </ReprintButton>
                </span>
              </Row>
            );
          })}
        </Table>
      )}
    </Wrapper>
  );
};

export default CloudPrintHistory;
